import { useState, useEffect, useRef } from "react"
import { useLazyQuery, useMutation } from "@apollo/client"
import useUser from "../../hooks/useUser"
import { Row, Col, Alert, Spinner } from "react-bootstrap"
import { useNavigate } from "react-router-dom"
import CustomButton from "../../components/CustomButton"

import GET_CONNECTIONS from "../../graphql/queries/courseCatalogue/getConnections"
import CREATE_HEI_CONNECTION from "../../graphql/mutations/courseCatalogue/createHEIConnection"
import CREATE_USER_CONNECTION from "../../graphql/mutations/courseCatalogue/createUserConnection"

import { useTranslation } from "react-i18next"

const CourseCatalogue = () => {
  const { t } = useTranslation('translation', {keyPrefix: 'courseCatalogue'})

  const { user } = useUser()
  const navigate = useNavigate()

  const userHasPermission = user?.permissions?.some(
    (item) => item.value === "manageCC"
  )

  const [connections, setConnections] = useState(null)
  const [heiConnection, setHeiConnection] = useState(null)
  const [userConnection, setUserConnection] = useState(null)
  const [backendInstance, setBackendInstance] = useState(null)
  const [statusMessage, setStatusMessage] = useState(null)

  // Mutation: Create HEI Connection
  const [createHEIConnection, { loading: loadingCreateHEIConnection }] =
    useMutation(CREATE_HEI_CONNECTION, {
      onCompleted: async () => {
        console.log("HEI Connection created")
        setStatusMessage(t('statusMessage.connectionCreated'))
        await refreshConnections()
      },
      onError: (error) =>
        console.error("Error creating HEI Connection:", error.message),
    })

  // Mutation: Create User Connection
  const [createUserConnection, { loading: loadingCreateUserConnection }] =
    useMutation(CREATE_USER_CONNECTION, {
      onCompleted: async () => {
        console.log("User Connection created")
        setStatusMessage(null)
        await refreshConnections()
      },
      onError: (error) =>
        console.error("Error creating User Connection:", error.message),
    })

  // Lazy query: Get connections
  const [
    loadGetConnections,
    {
      loading: loadingGetConnections,
      data: dataGetConnections,
      error: errorGetConnections,
      refetch: refetchConnections,
    },
  ] = useLazyQuery(GET_CONNECTIONS, {
    fetchPolicy: "cache-and-network",
    onCompleted: (data) => {
      if (data.getConnections?.heiConnection) {
        localStorage.setItem(
          "heiConnection",
          JSON.stringify(data.getConnections.heiConnection)
        )
        setHeiConnection(data.getConnections.heiConnection)
      } else {
        localStorage.removeItem("heiConnection")
        setHeiConnection(null)
      }

      if (data.getConnections?.userConnection) {
        localStorage.setItem(
          "userConnection",
          JSON.stringify(data.getConnections.userConnection)
        )
        setUserConnection(data.getConnections.userConnection)
      } else {
        localStorage.removeItem("userConnection")
        setUserConnection(null)
      }

      if (data.getConnections?.backendInstance) {
        localStorage.setItem(
          "backendInstance",
          JSON.stringify(data.getConnections.backendInstance)
        )
        setBackendInstance(data.getConnections.backendInstance)
      } else {
        localStorage.removeItem("backendInstance")
        setBackendInstance(null)
      }

      setConnections(data.getConnections)
    },
    onError: (error) =>
      console.error("Error loading Check Connections:", error.message),
  })

  // Prevent re-trigger of GET_CONNECTIONS
  const triggeredGetConnections = useRef(false)
  const triggeredCreateHei = useRef(false)
  const triggeredCreateUser = useRef(false)

  // 1. Read from localStorage and trigger GET_CONNECTIONS if necessary
  useEffect(() => {
    const heiConnectionStorage = JSON.parse(localStorage.getItem("heiConnection"))
    const userConnectionStorage = JSON.parse(localStorage.getItem("userConnection"))
    const backendInstanceStorage = JSON.parse(localStorage.getItem("backendInstance"))

    if(user?.userId !== userConnectionStorage?.backendUserName) {
      localStorage.removeItem("heiConnection")
      localStorage.removeItem("userConnection")
      localStorage.removeItem("backendInstance")
    }

    if (heiConnectionStorage) setHeiConnection(heiConnectionStorage)
    if (userConnectionStorage) setUserConnection(userConnectionStorage)

    if (
      userHasPermission &&
      !triggeredGetConnections.current &&
      (!heiConnectionStorage || !userConnectionStorage)
    ) {
      loadGetConnections({
        variables: {
          schacCode: user?.heiID,
          dashboardUserId: user?.userId,
        },
      })
      triggeredGetConnections.current = true
    }
  }, [])

  // Helper to refresh connections and update local state
  const refreshConnections = async () => {
    try {
      const { data: refreshed } = await refetchConnections({
        variables: {
          schacCode: user?.heiID,
          dashboardUserId: user?.userId,
        },
      })

      if (refreshed?.getConnections) {
        setConnections(refreshed.getConnections)

        if (refreshed.getConnections?.heiConnection) {
          localStorage.setItem(
            "heiConnection",
            JSON.stringify(refreshed.getConnections.heiConnection)
          )
          setHeiConnection(refreshed.getConnections.heiConnection)
        }
        if (refreshed.getConnections?.userConnection) {
          localStorage.setItem(
            "userConnection",
            JSON.stringify(refreshed.getConnections.userConnection)
          )
          setUserConnection(refreshed.getConnections.userConnection)
        }
        if (refreshed.getConnections?.backendInstance) {
          localStorage.setItem(
            "backendInstance",
            JSON.stringify(refreshed.getConnections.backendInstance)
          )
          setBackendInstance(refreshed.getConnections.backendInstance)
        }
      }

      return refreshed
    } catch (err) {
      console.error("Error refreshing connections:", err)
      return null
    }
  }

  // When connections change, automatically create HEI and/or User connections as needed
  useEffect(() => {
    if (!userHasPermission) return

    const shouldCreateHei =
      !!connections?.backendInstance && !heiConnection && !triggeredCreateHei.current

    if (shouldCreateHei) {
      (async () => {
        triggeredCreateHei.current = true
        setStatusMessage(
          t('statusMessage.creatingConnection')
        )
        try {
          await createHEIConnection({
            variables: {
              backendInstanceId: parseInt(
                connections.backendInstance?.id || backendInstance?.id || NaN
              ),
              dashboardHeiId: user?.erasmusCode,
              schacCode: user?.heiID,
            },
          })
        } catch (err) {
          console.error("createHEIConnection failed:", err)
        }
      })()
    }

    const existingHeiConnection = heiConnection || connections?.heiConnection
    const shouldCreateUser =
      !!existingHeiConnection && !userConnection && !triggeredCreateUser.current

    if (shouldCreateUser) {
      (async () => {
        triggeredCreateUser.current = true
        setStatusMessage(
          t('statusMessage.connectionDetected')
        )
        try {
          await createUserConnection({
            variables: {
              name: user?.userName,
              backendInstanceIdStr:
                existingHeiConnection?.backendInstance ||
                existingHeiConnection?.backendInstanceId ||
                backendInstance?.id,
              heiConnectionId: parseInt(existingHeiConnection?.id),
              dashboardUserId: user?.userId,
              backendHeiId: parseInt(
                existingHeiConnection?.backendHeiId ||
                  existingHeiConnection?.backendHeiIdStr ||
                  0
              ),
            },
          })
        } catch (err) {
          console.error("createUserConnection failed:", err)
        }
      })()
    }
  }, [connections, heiConnection, userConnection, userHasPermission])

  if (!userHasPermission) {
    return (
      <main className="h-auto text-center mt-4">
        <Row className="mt-5 pt-5 justify-content-center">
          <Col md={6}>
            <Alert key={"danger"} variant={"danger"}>
              {
                t('noPermissions')
              }
            </Alert>
          </Col>
        </Row>
      </main>
    )
  }

  return (
    <main className="h-auto text-center mt-4">
      {statusMessage && (
        <Row className="mt-5 pt-5 justify-content-center">
          <Col>
            <Spinner
              as="div"
              animation="grow"
              size="sm"
              role="status"
              aria-hidden="true"
              className="align-middle my-auto me-2"
            />
            {statusMessage}
          </Col>
        </Row>
      )}

      {loadingGetConnections && !statusMessage && (
        <Row className="mt-5 pt-5">
          <Col>
            <Spinner
              as="div"
              animation="grow"
              size="sm"
              role="status"
              aria-hidden="true"
              className="align-middle my-auto me-2"
            />
            {t('checkingConnections')}
          </Col>
        </Row>
      )}

      {!loadingGetConnections &&
        dataGetConnections &&
        !heiConnection &&
        !connections?.backendInstance && (
          <Row className="mt-5 pt-5 justify-content-center">
            <Col md={6}>
              <Alert key={"warning"} variant={"warning"}>
                {t('cannotConnect')}
              </Alert>
            </Col>
          </Row>
        )}

      {!loadingGetConnections && heiConnection && userConnection && (
        <>
          <Row className="mt-5 pt-5 justify-content-center">
            <Col md={6}>
              <Alert key={"success"} variant={"success"}>
                {
                  t('successfulConnection')
                }
              </Alert>
            </Col>
          </Row>

          <Row className="mt-5 justify-content-center">
            <Col md={6}>
              <p className="lead">{t('checkProgrammesAndCourses')}</p>
            </Col>
          </Row>

          <Row className="mt-3 justify-content-center">
            <Col md={2}>
              <p>
                <CustomButton
                  buttonType={"primary"}
                  text={t('programmesButton')}
                  handleClick={() => navigate("/cc/programmes")}
                />
              </p>
            </Col>
            <Col md={2}>
              <p>
                <CustomButton
                  buttonType={"primary"}
                  text={t('coursesButton')}
                  handleClick={() => navigate("/cc/courses")}
                />
              </p>
            </Col>
          </Row>
        </>
      )}

      {errorGetConnections && (
        <Row className="mt-5">
          <Col>{t('errorLoading')}</Col>
        </Row>
      )}
    </main>
  )
}

export default CourseCatalogue
